import { Add, DeleteOutline } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, styled, TextField, Typography } from '@mui/material';
import { makeValidate } from 'mui-rff';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ProjectConfigQuestion, ScopeFinding } from 'Types/dealGPT';
import * as Yup from 'yup';

import { AnalyticsEvent, useAnalytics } from '../../../../components/Providers/AnalyticsProvider';
import { selectFolders } from '../../../../redux/reducers/folderReducer';
import {
  deleteProjectConfigQuestion,
  saveProjectConfiguration,
  sowExtraction,
} from '../../../../services/dealGPT';
import FileDropzone from '../../../FileDropzone';
import ScopeSelector from './ScopeSelector';

const StyledForm = styled('form')({
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  margin: '0 20px 20px 20px',
});

const StyledButton = styled(Button)({
  width: '120px',
  height: '35px',
});

const ButtonContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '5px',
  marginTop: '25px',
});

const schema = Yup.object({
  investmentThesis: Yup.string().required('This field is required'),
  keyQuestions: Yup.array().of(
    Yup.object({
      question: Yup.string().required('Question is required'),
    })
  ),
});

const validate = makeValidate(schema);

const defaultInvestmentThesis =
  'The proposed acquisition offers potential for value creation over a 5-year hold period through organic growth initiatives.';

interface ProjectConfigProps {
  keyQuestions: ProjectConfigQuestion[];
  investmentThesis: string;
  projectId: string;
}

const ProjectConfig: React.FC<ProjectConfigProps> = ({ keyQuestions, investmentThesis }) => {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { currentFolder } = useSelector(selectFolders);

  const [currentInvestmentThesis, setCurrentInvestmentThesis] = useState<string>(
    investmentThesis || defaultInvestmentThesis
  );
  const [sowScopes, setSOWScopes] = useState<ScopeFinding[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<ScopeFinding[]>([]);
  const [questions, setQuestions] = useState<ProjectConfigQuestion[]>(keyQuestions);

  const [isExtracting, setIsExtracting] = useState(false);

  useEffect(() => {
    setQuestions(keyQuestions);
    setCurrentInvestmentThesis(investmentThesis || defaultInvestmentThesis);
  }, [keyQuestions, investmentThesis]);

  const initialQuestion: ProjectConfigQuestion = {
    id: undefined,
    question: '',
    projectId: currentFolder?.projectId ?? '',
    deleted: false,
  };

  const handleAddQuestion = () => {
    analytics.event(
      AnalyticsEvent.WM_IA_PROJECT_CCONFIG_KEY_ADD,
      {
        id: currentFolder?.projectId,
      },
      {
        projectId: currentFolder?.projectId,
      }
    );
    setQuestions([...questions, { ...initialQuestion, isNew: true }]);
  };

  const allQuestionsHaveContent = questions.every((question) => question.question.trim() !== '');

  const handleChangeQuestion = (index: number, newQuestion: string) => {
    const updatedQuestions = questions.map((item, i) =>
      i === index ? { ...item, question: newQuestion, isModified: true } : item
    );
    setQuestions(updatedQuestions);
  };

  const handleDeleteQuestion = async (index: number) => {
    analytics.event(
      AnalyticsEvent.WM_IA_PROJECT_CONFIG_KEY_DELETE,
      {
        id: currentFolder?.projectId,
      },
      {
        projectId: currentFolder?.projectId,
      }
    );
    const questionToDelete = questions[index];
    if (questionToDelete.id && currentFolder?.projectId) {
      await deleteProjectConfigQuestion(currentFolder?.projectId, questionToDelete.id);
    }
    const updatedQuestions = questions.filter((_, i) => i !== index);

    setQuestions(updatedQuestions);
  };

  const handleSubmit = async (values: { investmentThesis: string }) => {
    analytics.event(
      AnalyticsEvent.WM_IA_PROJECT_CONFIG_KEY_SAVE,
      {
        id: currentFolder?.projectId,
      },
      {
        projectId: currentFolder?.projectId,
      }
    );
    const changedQuestions = questions.filter((q) => q.isNew || q.isModified);

    if (questions.length === 0) {
      enqueueSnackbar('At least one key question is required', { variant: 'error' });
      return;
    }

    if (currentFolder?.projectId) {
      try {
        const response = await saveProjectConfiguration(currentFolder.projectId, {
          keyQuestions: changedQuestions,
          investmentThesis: values.investmentThesis,
          projectId: currentFolder.projectId,
          scopes: selectedScopes,
        });

        if (response.length > 0) {
          const resetQuestions = questions.map((q) => ({
            ...q,
            isNew: false,
            isModified: false,
          }));
          setQuestions(response.length > 0 ? response : resetQuestions);
          enqueueSnackbar('Project configuration saved successfully', { variant: 'success' });
          navigate(`/folder/${currentFolder?.id}`);
        } else {
          enqueueSnackbar('No changes were made to save', { variant: 'info' });
        }
      } catch (error) {
        enqueueSnackbar('Error saving project configuration', { variant: 'error' });
      }
    } else {
      enqueueSnackbar('Project ID is missing', { variant: 'error' });
    }
  };

  const processSOW = useCallback(
    async (files: File[]) => {
      const [file] = files;
      try {
        if (!currentFolder || !currentFolder.projectId) {
          return;
        }
        setIsExtracting(true);

        analytics.event(
          AnalyticsEvent.WM_IA_P_CONFIG_SOW_EXTRACTIONS,
          {
            id: currentFolder.projectId,
            value: `fileName=${file.name}`,
          },
          {
            projectId: currentFolder.projectId,
            fileName: file.name,
          }
        );

        const result = await sowExtraction(file, currentFolder.projectId);
        if (result.investmentThesis?.thesis) {
          setCurrentInvestmentThesis(result.investmentThesis.thesis);
        }
        setSOWScopes(result.scopes);
        const sowQuestions = result.keyQuestions.map(
          (question) =>
            ({
              question,
              projectId: currentFolder.projectId,
              deleted: false,
              isNew: true,
              createdAt: new Date(),
            } as ProjectConfigQuestion)
        );
        setQuestions((prevQuestions) => [
          ...sowQuestions,
          // If there are extracted questions, filter out question, if not leave it as is
          ...prevQuestions.filter((q) =>
            sowQuestions.length > 0 ? q.question.trim() !== '' : true
          ),
        ]);
      } catch (error) {
        enqueueSnackbar('Failed to extract information from your SOW', { variant: 'error' });
      } finally {
        setIsExtracting(false);
      }
    },
    [currentFolder, analytics]
  );

  return (
    <>
      <Form
        onSubmit={handleSubmit}
        initialValues={{
          investmentThesis: currentInvestmentThesis,
          keyQuestions: questions,
        }}
        validate={validate}
        render={({ handleSubmit }) => (
          <StyledForm onSubmit={handleSubmit} noValidate>
            <FileDropzone label="Drag and Drop your SOW" onFilesAdded={processSOW} />
            <Field name="investmentThesis">
              {({ input, meta }) => (
                <TextField
                  label="Investment Thesis"
                  {...input}
                  error={meta.touched && !!meta.error}
                  helperText={meta.touched && meta.error ? meta.error : ''}
                  multiline
                  required
                  margin="normal"
                  variant="outlined"
                  onChange={(e) => {
                    input.onChange(e);
                    setCurrentInvestmentThesis(e.target.value);
                  }}
                />
              )}
            </Field>
            <br />
            <Box sx={{ display: 'flex', gap: 2 }}>
              {currentFolder?.project && (
                <>
                  <ScopeSelector
                    project={currentFolder.project}
                    sowScopes={sowScopes}
                    selectedScopes={selectedScopes}
                    setSelectedScopes={setSelectedScopes}
                  />
                  <Divider orientation="vertical" flexItem />
                </>
              )}

              <Box sx={{ flexGrow: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: '500' }}>Key Questions</Typography>
                {questions.map((question, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Field
                      name={`keyQuestions[${index}].question`}
                      render={({ input, meta }) => (
                        <TextField
                          label={`Enter Key Question ${index + 1}`}
                          {...input}
                          error={meta.touched && !!meta.error}
                          value={question.question}
                          helperText={meta.touched && meta.error ? meta.error : ''}
                          onChange={(e) => handleChangeQuestion(index, e.target.value)}
                          required
                          fullWidth
                          margin="normal"
                          variant="outlined"
                          multiline
                        />
                      )}
                    />
                    <IconButton onClick={() => handleDeleteQuestion(index)} disabled={isExtracting}>
                      <DeleteOutline />
                    </IconButton>
                  </div>
                ))}
              </Box>
            </Box>
            <ButtonContainer>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddQuestion}
                startIcon={<Add />}
                disabled={isExtracting}
              >
                Add Key Question
              </Button>
              <StyledButton
                type="submit"
                variant="contained"
                disabled={
                  !currentInvestmentThesis.trim() || !allQuestionsHaveContent || isExtracting
                }
              >
                Save
              </StyledButton>
            </ButtonContainer>
          </StyledForm>
        )}
      />
    </>
  );
};

export default ProjectConfig;
